import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import { getVolume, setVolume as setSoundVolume, isMuted, setMuted as setSoundMuted, playSfx } from '../utils/sound';
import './UserSettings.css';

interface AccountInfo {
  username: string;
  email: string;
  role: string;
  createdAt: string;
}

const UserSettings: React.FC = () => {
  const { user, updateUser, handleAuthError } = useAuth();

  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [volume, setVolume] = useState(() => getVolume());
  const [muted, setMuted] = useState(() => isMuted());

  useEffect(() => {
    const fetchMe = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await makeAuthenticatedRequest('/account/me');
        if (res.ok) setAccountInfo(await res.json());
      } catch (err) {
        if (handleApiError(err).isAuthError) handleAuthError();
      }
    };
    fetchMe();
  }, [handleAuthError]);

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');
    setEmailLoading(true);
    try {
      const res = await makeAuthenticatedRequest('/account/change-email', {
        method: 'POST',
        body: JSON.stringify({ newEmail }),
      });
      const data = await res.json();
      if (!res.ok) { setEmailError(data.message || 'Email change failed.'); return; }
      updateUser({ username: data.username, email: data.email, role: data.role }, data.token);
      setAccountInfo(prev => prev ? { ...prev, email: data.email } : prev);
      setNewEmail('');
      setEmailSuccess('Email updated successfully.');
    } catch (err) {
      if (handleApiError(err).isAuthError) { handleAuthError(); return; }
      setEmailError('Network error. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await makeAuthenticatedRequest('/account/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setPasswordError(data.message || 'Password change failed.'); return; }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess(data.message || 'Password updated successfully.');
    } catch (err) {
      if (handleApiError(err).isAuthError) { handleAuthError(); return; }
      setPasswordError('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Number(e.target.value) / 100;
    setVolume(next);
    setSoundVolume(next);
  };

  const handleVolumeCommit = () => {
    if (!muted) playSfx('uiClick');
  };

  const handleMuteToggle = () => {
    const next = !muted;
    setMuted(next);
    setSoundMuted(next);
    if (!next) playSfx('uiClick');
  };

  return (
    <div className="us-panel">
      <div className="us-panel-header">
        <h2>Account Settings</h2>
        <p>Manage your login credentials and view account details.</p>
      </div>

      <div className="us-body">
        {/* ── Account info ── */}
        <section>
          <div className="us-section-header us-section-header--gold">Account</div>
          <div className="us-info-grid">
            <div className="us-info-cell">
              <span className="us-label">Username</span>
              <span className="us-value">{accountInfo?.username ?? user?.username ?? '—'}</span>
            </div>
            <div className="us-info-cell">
              <span className="us-label">Role</span>
              <span className="us-value">{accountInfo?.role ?? user?.role ?? '—'}</span>
            </div>
            <div className="us-info-cell">
              <span className="us-label">Email</span>
              <span className="us-value">{accountInfo?.email ?? user?.email ?? '—'}</span>
            </div>
            <div className="us-info-cell">
              <span className="us-label">Member Since</span>
              <span className="us-value">
                {accountInfo ? formatDate(accountInfo.createdAt) : '—'}
              </span>
            </div>
          </div>
        </section>

        {/* ── Sound ── */}
        <section>
          <div className="us-section-header">Sound</div>
          <div className="us-form-group">
            <label htmlFor="us-sound-volume">Volume ({Math.round(volume * 100)}%)</label>
            <input
              id="us-sound-volume"
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round(volume * 100)}
              onChange={handleVolumeChange}
              onMouseUp={handleVolumeCommit}
              onTouchEnd={handleVolumeCommit}
              disabled={muted}
            />
          </div>
          <div className="us-form-group">
            <label>
              <input
                type="checkbox"
                checked={muted}
                onChange={handleMuteToggle}
              />
              {' '}Mute all sound effects
            </label>
          </div>
        </section>

        {/* ── Change email ── */}
        <section>
          <div className="us-section-header">Change Email</div>
          <form className="us-form" onSubmit={handleChangeEmail}>
            {emailError && <div className="us-error">{emailError}</div>}
            {emailSuccess && <div className="us-success">{emailSuccess}</div>}
            <div className="us-form-group">
              <label htmlFor="us-new-email">New Email Address</label>
              <input
                id="us-new-email"
                type="email"
                value={newEmail}
                onChange={e => { setNewEmail(e.target.value); setEmailSuccess(''); }}
                placeholder="Enter new email address"
                required
                disabled={emailLoading}
              />
            </div>
            <button type="submit" className="us-btn-save" disabled={emailLoading}>
              {emailLoading ? 'Saving…' : 'Save Email'}
            </button>
          </form>
        </section>

        {/* ── Change password ── */}
        <section>
          <div className="us-section-header">Change Password</div>
          <form className="us-form" onSubmit={handleChangePassword}>
            {passwordError && <div className="us-error">{passwordError}</div>}
            {passwordSuccess && <div className="us-success">{passwordSuccess}</div>}
            <div className="us-form-group">
              <label htmlFor="us-current-password">Current Password</label>
              <input
                id="us-current-password"
                type="password"
                value={currentPassword}
                onChange={e => { setCurrentPassword(e.target.value); setPasswordSuccess(''); }}
                placeholder="Enter current password"
                required
                disabled={passwordLoading}
              />
            </div>
            <div className="us-form-group">
              <label htmlFor="us-new-password">New Password</label>
              <input
                id="us-new-password"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                disabled={passwordLoading}
              />
            </div>
            <div className="us-form-group">
              <label htmlFor="us-confirm-password">Confirm New Password</label>
              <input
                id="us-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                required
                disabled={passwordLoading}
              />
            </div>
            <button type="submit" className="us-btn-save" disabled={passwordLoading}>
              {passwordLoading ? 'Saving…' : 'Save Password'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default UserSettings;
